import { useEffect, useState } from 'react'

import { Alert, Badge, CodeBlock, Tabs, cx } from '../components/ui'
import { request } from '../lib/api'

interface ErrorItem {
  code: string
  status: number
  message: string
}

type Lang = 'curl' | 'node' | 'python' | 'php'

const SNIPPETS: Record<Lang, string> = {
  curl: `curl -X POST https://qr24.app/api/v1/qr/generate \\
  -H "Authorization: Bearer $QR24_KEY" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "Content-Type: application/json" \\
  -d '{
    "version": "v2",
    "payment": {
      "beneficiaryName": "ТОВ \\"Ромашка\\"",
      "iban": "UA213223130000026007233566001",
      "beneficiaryCode": "32129530",
      "amount": "1250.00",
      "purpose": "Оплата за рахунком №12/8"
    },
    "render": { "format": "png", "scale": 10, "ecc": "M" }
  }' --output qr.png`,

  node: `import { randomUUID } from 'node:crypto'
import { writeFile } from 'node:fs/promises'

const res = await fetch('https://qr24.app/api/v1/qr/generate', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.QR24_KEY}\`,
    'Idempotency-Key': randomUUID(),
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    version: 'v2',
    payment: {
      beneficiaryName: 'ТОВ "Ромашка"',
      iban: 'UA213223130000026007233566001',
      beneficiaryCode: '32129530',
      amount: '1250.00',
      purpose: 'Оплата за рахунком №12/8',
    },
    render: { format: 'png', scale: 10 },
  }),
})

if (!res.ok) {
  const { error } = await res.json()
  throw new Error(\`\${error.code}: \${error.message}\`)
}

// Залишок квоти доступний без окремого запиту
console.log('Залишилось:', res.headers.get('X-Quota-Remaining'))
await writeFile('qr.png', Buffer.from(await res.arrayBuffer()))`,

  python: `import os, uuid, httpx

resp = httpx.post(
    "https://qr24.app/api/v1/qr/generate",
    headers={
        "Authorization": f"Bearer {os.environ['QR24_KEY']}",
        "Idempotency-Key": str(uuid.uuid4()),
    },
    json={
        "version": "v2",
        "payment": {
            "beneficiaryName": 'ТОВ "Ромашка"',
            "iban": "UA213223130000026007233566001",
            "beneficiaryCode": "32129530",
            "amount": "1250.00",
            "purpose": "Оплата за рахунком №12/8",
        },
        "render": {"format": "png", "scale": 10},
    },
    timeout=15,
)

if resp.status_code >= 400:
    error = resp.json()["error"]
    raise RuntimeError(f"{error['code']}: {error['message']}")

print("Залишилось:", resp.headers["X-Quota-Remaining"])
open("qr.png", "wb").write(resp.content)`,

  php: `<?php
$ch = curl_init('https://qr24.app/api/v1/qr/generate');
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        'Authorization: Bearer ' . getenv('QR24_KEY'),
        'Idempotency-Key: ' . bin2hex(random_bytes(16)),
        'Content-Type: application/json',
    ],
    CURLOPT_POSTFIELDS => json_encode([
        'version' => 'v2',
        'payment' => [
            'beneficiaryName' => 'ТОВ "Ромашка"',
            'iban'            => 'UA213223130000026007233566001',
            'beneficiaryCode' => '32129530',
            'amount'          => '1250.00',
            'purpose'         => 'Оплата за рахунком №12/8',
        ],
        'render' => ['format' => 'png', 'scale' => 10],
    ], JSON_UNESCAPED_UNICODE),
]);

$body = curl_exec($ch);
$code = curl_getinfo($ch, CURLINFO_HTTP_CODE);

if ($code >= 400) {
    $err = json_decode($body, true)['error'];
    throw new RuntimeException("{$err['code']}: {$err['message']}");
}

file_put_contents('qr.png', $body);`,
}

const BATCH_SNIPPET = `POST /api/v1/qr/batch
{
  "version": "v2",
  "render": { "format": "dataurl", "scale": 6 },
  "atomic": false,
  "items": [
    { "ref": "invoice-1001", "beneficiaryName": "ТОВ \\"Ромашка\\"",
      "iban": "UA213223130000026007233566001", "beneficiaryCode": "32129530",
      "amount": "199.00", "purpose": "Рахунок 1001" },
    { "ref": "invoice-1002", "beneficiaryName": "ТОВ \\"Ромашка\\"",
      "iban": "UA213223130000026007233566001", "beneficiaryCode": "32129530",
      "amount": "349.50", "purpose": "Рахунок 1002" }
  ]
}

// Відповідь
{
  "total": 2, "succeeded": 2, "failed": 0,
  "results": [
    { "ref": "invoice-1001", "index": 0, "ok": true, "payload": "BCD\\n002\\n...", "image": { ... } },
    { "ref": "invoice-1002", "index": 1, "ok": true, "payload": "BCD\\n002\\n...", "image": { ... } }
  ],
  "quota": { "included": 10000, "used": 42, "remaining": 9958 }
}`

const ERROR_SHAPE = `{
  "error": {
    "code": "qr_spec_violation",
    "message": "Контрольна сума IBAN не збігається",
    "details": {
      "field": "iban",
      "hint": "Перевірте, чи не переплутані цифри — контроль за ISO 13616 (mod 97)."
    }
  }
}`

const SECTIONS = [
  { id: 'start', label: 'Швидкий старт' },
  { id: 'auth', label: 'Автентифікація' },
  { id: 'generate', label: 'Генерація' },
  { id: 'batch', label: 'Пакетна генерація' },
  { id: 'decode', label: 'Валідація і розбір' },
  { id: 'idempotency', label: 'Ідемпотентність' },
  { id: 'quota', label: 'Квоти й ліміти' },
  { id: 'errors', label: 'Помилки' },
]

export function Docs() {
  const [lang, setLang] = useState<Lang>('curl')
  const [errors, setErrors] = useState<ErrorItem[]>([])

  useEffect(() => {
    request<{ items: ErrorItem[] }>('/meta/errors', { auth: false })
      .then((r) => setErrors(r.items))
      .catch(() => undefined)
  }, [])

  return (
    <div className="container-page grid gap-10 py-12 lg:grid-cols-[220px_1fr]">
      <nav className="hidden lg:block">
        <div className="sticky top-24">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Розділи
          </p>
          <ul className="space-y-1 text-sm">
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="block rounded-lg px-3 py-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                >
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
          <a href="/api/docs" className="btn-secondary mt-6 w-full text-xs">
            Swagger UI ↗
          </a>
        </div>
      </nav>

      <article className="min-w-0 max-w-3xl">
        <h1 className="text-4xl font-bold tracking-tight">Документація API</h1>
        <p className="mt-3 text-lg text-slate-600 dark:text-slate-400">
          REST, JSON, жодних SDK. Базовий URL —{' '}
          <code className="code">https://qr24.app/api/v1</code>
        </p>

        <Section id="start" title="Швидкий старт">
          <ol className="mb-6 space-y-3 text-sm">
            {[
              'Зареєструйтеся — акаунт одразу отримує ключ qr24_test_…, який не тарифікується.',
              'Створіть бойовий ключ у кабінеті, у розділі «API-ключі».',
              'Надішліть запит нижче — і отримайте готовий PNG.',
            ].map((step, i) => (
              <li key={step} className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-600 text-xs font-bold text-white">
                  {i + 1}
                </span>
                <span className="text-slate-600 dark:text-slate-300">{step}</span>
              </li>
            ))}
          </ol>

          <div className="mb-3">
            <Tabs<Lang>
              value={lang}
              onChange={setLang}
              tabs={[
                { value: 'curl', label: 'cURL' },
                { value: 'node', label: 'Node.js' },
                { value: 'python', label: 'Python' },
                { value: 'php', label: 'PHP' },
              ]}
            />
          </div>
          <CodeBlock code={SNIPPETS[lang]} title={lang} />
        </Section>

        <Section id="auth" title="Автентифікація">
          <p>Ключ передається одним із двох способів — обидва рівноцінні:</p>
          <CodeBlock
            title="headers"
            code={`Authorization: Bearer qr24_live_7f3a9c1b2d4e.uJ8k…\nX-API-Key: qr24_live_7f3a9c1b2d4e.uJ8k…`}
          />
          <Alert tone="warning" title="Ключ показується один раз">
            Секретна частина не зберігається у нас у відкритому вигляді — після створення
            її неможливо переглянути повторно. Загубили — зробіть ротацію.
          </Alert>
          <p className="mt-4">
            Ключі бувають двох середовищ: <code className="code">qr24_live_…</code> списує квоту,{' '}
            <code className="code">qr24_test_…</code> — ні. Тестовий проходить ту саму валідацію
            й повертає той самий payload, тому його безпечно тримати в CI.
          </p>
        </Section>

        <Section id="generate" title="POST /qr/generate">
          <p>
            Формати <code className="code">png</code>, <code className="code">pdf</code>,{' '}
            <code className="code">svg</code>, <code className="code">eps</code> повертають
            файл напряму — тіло відповіді і є зображенням. Формат{' '}
            <code className="code">dataurl</code> повертає JSON із вбудованою картинкою та
            повним payload.
          </p>
          <ParamTable
            rows={[
              ['version', 'v1 | v2 | v3', 'Версія формату НБУ (001/002/003). За замовчуванням v2.'],
              ['charset', '1 | 2', '1 — UTF-8 (рекомендовано), 2 — Windows-1251.'],
              ['payment.beneficiaryName', 'string', 'Отримувач. Обовʼязково. 70 симв. (v1/v2), 140 (v3).'],
              ['payment.iban', 'string', 'IBAN отримувача, 29 символів. Обовʼязково.'],
              ['payment.beneficiaryCode', 'string', 'ЄДРПОУ (8 цифр) або РНОКПП (10). Обовʼязково.'],
              ['payment.purpose', 'string', 'Призначення. Обовʼязково. 140 симв. (v1/v2), 420 (v3).'],
              ['payment.amount', 'string', 'Сума у гривнях. Порожньо — платник вводить сам.'],
              ['payment.reference', 'string', 'Ваш ідентифікатор рахунку, до 35 симв. (з v2).'],
              ['payment.displayInfo', 'string', 'Текст для платника. 70 симв. (v1/v2), 140 (v3).'],
              ['payment.function', 'UCT | ICT | XCT', 'Тип переказу. ICT/XCT — лише v3.'],
              ['payment.category', 'CCCC/PPPP', 'Категорія ISO 20022. Лише v3.'],
              ['payment.lockMask', 'hex(4)', 'Заборона зміни полів платником. Лише v3.'],
              ['payment.validUntil', 'ISO 8601', 'Дата/час, після якого QR недійсний. Лише v3.'],
              ['render.format', 'string', 'png | svg | pdf | eps | txt | dataurl'],
              ['render.ecc', 'L | M | Q | H', 'Рівень корекції. H — обовʼязково з логотипом.'],
              ['render.scale', '1 … 40', 'Пікселів на модуль.'],
              ['render.logo', 'base64', 'Логотип у центрі. Потребує ecc=Q або H.'],
              ['save', 'boolean', 'Зберегти в історії та видати коротке посилання.'],
            ]}
          />

          <Alert tone="warning" title="Ліміт payload рахується в байтах, а не символах">
            Кирилична літера в UTF-8 займає 2 байти. Формат 003 дозволяє 420 символів
            призначення, але весь payload обмежений 507 байтами — повністю заповнене
            українське призначення туди не влізе. Використовуйте{' '}
            <code className="code">/qr/validate</code>, щоб побачити фактичний розмір.
          </Alert>
        </Section>

        <Section id="batch" title="POST /qr/batch">
          <p>
            До 500 QR за запит (залежно від тарифу). Поле <code className="code">ref</code>{' '}
            повертається без змін — за ним зручно зіставляти результати з вашими рахунками.
            Тарифікуються лише успішні рядки.
          </p>
          <CodeBlock code={BATCH_SNIPPET} title="batch" />
          <Alert tone="brand">
            З <code className="code">atomic: true</code> увесь пакет відхиляється, якщо хоч
            один рядок невалідний — зручно, коли часткова генерація неприйнятна.
          </Alert>
        </Section>

        <Section id="decode" title="Валідація і розбір">
          <p>
            <code className="code">POST /qr/validate</code> перевіряє реквізити й повертає
            payload без рендера зображення. <code className="code">POST /qr/decode</code>{' '}
            робить зворотне: перетворює відсканований текст на поля й перелічує проблеми.
          </p>
          <Alert tone="success">
            Обидва ендпоінти безкоштовні та не витрачають квоту — викликайте їх прямо з
            форми на своєму сайті.
          </Alert>
        </Section>

        <Section id="idempotency" title="Ідемпотентність">
          <p>
            Надішліть <code className="code">Idempotency-Key: &lt;uuid&gt;</code> — і повтор
            після таймауту поверне збережену відповідь замість другого списання. Запис
            живе 24 години.
          </p>
          <ul className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
            <li>• Той самий ключ і те саме тіло → відповідь відтворюється, квота не списується.</li>
            <li>
              • Той самий ключ, але <b>інше</b> тіло → <code className="code">409 idempotency_key_reuse</code>.
            </li>
            <li>
              • Запит ще виконується → <code className="code">409 idempotency_in_flight</code>, повторіть за 2 с.
            </li>
          </ul>
        </Section>

        <Section id="quota" title="Квоти й ліміти">
          <p>Кожна відповідь несе поточний стан балансу — окремий запит не потрібен:</p>
          <CodeBlock
            title="response headers"
            code={`X-Quota-Limit:     10000\nX-Quota-Remaining: 9958\nX-Quota-Credits:   5000\nX-Quota-Reset:     2026-09-01T00:00:00\nX-Request-Id:      3f9a1c…`}
          />
          <p className="mt-4">
            Порядок списання: спершу місячна квота тарифу, потім передплачені кредити, далі —
            тарифікація понад ліміт (на тарифах, де вона увімкнена). Якщо платити нічим,
            API поверне <code className="code">402 quota_exceeded</code>.
          </p>
          <p className="mt-3">
            При перевищенні частоти повертається <code className="code">429</code> із
            заголовком <code className="code">Retry-After</code> у секундах.
          </p>
        </Section>

        <Section id="errors" title="Помилки">
          <p>Структура однакова для всіх помилок:</p>
          <CodeBlock code={ERROR_SHAPE} title="error" />
          <p className="mt-4">
            Поле <code className="code">details.field</code> присутнє скрізь, де помилка
            стосується конкретного реквізиту — його можна одразу підсвітити у формі.
          </p>

          {errors.length > 0 && (
            <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">HTTP</th>
                    <th className="px-4 py-2.5 font-medium">Код</th>
                    <th className="px-4 py-2.5 font-medium">Значення</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {errors.map((error) => (
                    <tr key={error.code}>
                      <td className="px-4 py-2.5">
                        <Badge
                          tone={
                            error.status >= 500
                              ? 'danger'
                              : error.status === 429 || error.status === 402
                                ? 'warning'
                                : 'neutral'
                          }
                        >
                          {error.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">{error.code}</td>
                      <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">
                        {error.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </article>
    </div>
  )
}

function Section({
  id,
  title,
  children,
}: {
  id: string
  title: string
  children: React.ReactNode
}) {
  return (
    <section id={id} className="mt-14 scroll-mt-24">
      <h2 className="mb-4 border-b border-slate-200 pb-2 text-2xl font-bold tracking-tight dark:border-slate-800">
        {title}
      </h2>
      <div className="space-y-4 text-slate-600 dark:text-slate-300 [&>p]:leading-relaxed">
        {children}
      </div>
    </section>
  )
}

function ParamTable({ rows }: { rows: [string, string, string][] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
      <table className="w-full min-w-[520px] text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
          <tr>
            <th className="px-4 py-2.5 font-medium">Поле</th>
            <th className="px-4 py-2.5 font-medium">Тип</th>
            <th className="px-4 py-2.5 font-medium">Опис</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
          {rows.map(([name, type, description]) => (
            <tr key={name}>
              <td className={cx('px-4 py-2.5 font-mono text-xs text-brand-700 dark:text-brand-300')}>
                {name}
              </td>
              <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{type}</td>
              <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
